/**
 * Mobile Slide Menu - GNB 하단 2depth 패널 메뉴
 *
 * @extends {HTMLElement}
 */
class MobileSlideMenu extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLElement | null} */
    this.container = this.querySelector(".mobile-slide-menu__container");
    /** @type {HTMLElement | null} */
    this.panelsContainer = this.querySelector("[data-panels]");
    /** @type {NodeListOf<HTMLElement>} */
    this.panels = this.querySelectorAll("[data-panel]");

    /** @type {string[]} */
    this.history = [];
    /** @type {string} */
    this.currentPanelId = "main";
    /** @type {boolean} */
    this.isOpen = false;

    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("[data-goto-panel]").forEach((el) => {
      const btn = /** @type {HTMLElement} */ (el);
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const targetId = btn.dataset.gotoPanel;
        const parentId = btn.dataset.parentId;
        if (targetId) {
          this.goToPanel(targetId, parentId || "");
        }
      });
    });

    this.querySelectorAll("[data-back]").forEach((el) => {
      const btn = /** @type {HTMLElement} */ (el);
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        this.goBack();
      });
    });

    this.querySelectorAll("a[href]").forEach((el) => {
      el.addEventListener("click", (e) => {
        const href = el.getAttribute("href");
        if (!href) return;
        e.preventDefault();
        this.close();
        setTimeout(() => {
          window.location.href = href;
        }, 0);
      });
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    this.setupSwipeToClose();
  }

  /**
   * 위로 스와이프하여 메뉴 닫기
   */
  setupSwipeToClose() {
    let startY = 0;
    let currentY = 0;
    let isDragging = false;
    let moved = false;

    this.container?.addEventListener(
      "touchstart",
      (e) => {
        if (!(e instanceof TouchEvent) || e.touches.length === 0) return;
        startY = e.touches[0].clientY;
        currentY = startY;
        isDragging = true;
        moved = false;
      },
      { passive: true }
    );

    this.container?.addEventListener(
      "touchmove",
      (e) => {
        if (!isDragging) return;
        if (e instanceof TouchEvent && e.touches.length > 0) {
          currentY = e.touches[0].clientY;
          moved = true;
        }
      },
      { passive: true }
    );

    this.container?.addEventListener(
      "touchend",
      () => {
        if (!isDragging || !moved) return;
        isDragging = false;
        const diff = startY - currentY;
        if (diff > 80) {
          this.close();
        }
      },
      { passive: true }
    );
  }

  /**
   * @param {string} panelId
   * @param {string} parentId
   */
  goToPanel(panelId, parentId) {
    const currentPanel = /** @type {HTMLElement | null} */ (
      this.querySelector(`[data-panel-id="${this.currentPanelId}"]`)
    );
    const targetPanel = /** @type {HTMLElement | null} */ (
      this.querySelector(`[data-panel-id="${panelId}"]`)
    );

    if (!targetPanel) {
      console.warn(`Panel not found: ${panelId}`);
      return;
    }

    if (currentPanel) {
      currentPanel.dataset.active = "false";
      currentPanel.dataset.pushed = "true";
    }

    targetPanel.dataset.active = "true";
    targetPanel.dataset.pushed = "false";

    this.history.push(this.currentPanelId);
    this.currentPanelId = panelId;

    const firstFocusable = /** @type {HTMLElement | null} */ (
      targetPanel.querySelector("button, a")
    );
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 300);
    }
  }

  goBack() {
    if (this.history.length === 0) {
      this.close();
      return;
    }

    const currentPanel = /** @type {HTMLElement | null} */ (
      this.querySelector(`[data-panel-id="${this.currentPanelId}"]`)
    );
    const prevPanelId = this.history.pop() || "main";
    const prevPanel = /** @type {HTMLElement | null} */ (
      this.querySelector(`[data-panel-id="${prevPanelId}"]`)
    );

    if (!prevPanel) {
      console.warn(`Previous panel not found: ${prevPanelId}`);
      return;
    }

    if (currentPanel) {
      currentPanel.dataset.active = "false";
      currentPanel.dataset.pushed = "false";
    }

    prevPanel.dataset.active = "true";
    prevPanel.dataset.pushed = "false";

    this.currentPanelId = prevPanelId;

    const backButton = /** @type {HTMLElement | null} */ (
      prevPanel.querySelector("[data-back]")
    );
    if (backButton) {
      setTimeout(() => backButton.focus(), 300);
    }
  }

  open() {
    this.isOpen = true;
    this.setAttribute("aria-hidden", "false");
    document.body.classList.add("mobile-slide-menu-open");
    // 중요: data-menu-open을 먼저 설정해야 CSS(:has 규칙)가 #header-component의
    // transform을 즉시 none으로 만든다. 그 상태에서 위치를 측정해야 fixed 메뉴의
    // 위치 기준(containing block)이 viewport로 고정되어 헤더와 딱 붙는다.
    this._syncDrawerState(true);
    this._updatePosition();

    const firstFocusable = /** @type {HTMLElement | null} */ (
      this.querySelector("button, a")
    );
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 300);
    }

    this.dispatchEvent(new CustomEvent("menu:opened", { bubbles: true }));
  }

  close() {
    this.isOpen = false;
    this.setAttribute("aria-hidden", "true");
    document.body.classList.remove("mobile-slide-menu-open");
    this._syncDrawerState(false);

    setTimeout(() => {
      this.style.top = "";
      this.style.height = "";
      this.resetToMain();
    }, 300);

    this.dispatchEvent(new CustomEvent("menu:closed", { bubbles: true }));
  }

  /**
   * GNB 헤더의 햄버거/X 토글 상태 동기화
   * @param {boolean} isOpen
   */
  _syncDrawerState(isOpen) {
    const drawer = this.closest(".header__drawer");
    if (drawer) {
      if (isOpen) {
        drawer.setAttribute("data-menu-open", "true");
      } else {
        drawer.removeAttribute("data-menu-open");
      }
    }
  }

  /**
   * 헤더 전체의 하단(bottom)에 슬라이드 메뉴가 정확히 붙도록 top/height를 동적 설정한다.
   *
   * 핵심 배경: mobile-slide-menu는 position:fixed 이지만 DOM상 #header-component 내부에 있다.
   *  - #header-component가 transform(스크롤 숨김 등)을 가지면 fixed의 위치 기준(containing block)이
   *    viewport가 아닌 헤더 자신으로 바뀌어 버린다.
   *  - 그러면 JS로 구한 viewport 기준 bottom과 실제 fixed 기준이 어긋나 헤더·메뉴 사이에
   *    간격이 벌어진다(이전에 "translateY(0) → none"으로 임시 조치했던 것과 같은 원인).
   *
   * 따라서 open()에서 _syncDrawerState(true)로 CSS가 헤더의 transform을 none으로 만든 뒤 이 메서드를
   * 실행하며, 추가로 만약의 경우(다른 조상이 transform/filter 등을 가질 때)를 대비해 containing block
   * offset만큼 top을 보정한다. 또한 단일 row가 아닌 #header-component 전체(모든 row 포함)의 하단을
   * 기준으로 삼아, 여러 row로 구성된 모바일 헤더에서도 항상 헤더 바로 아래에 붙는다.
   */
  _updatePosition() {
    const header = /** @type {HTMLElement | null} */ (
      this.closest('#header-component') || document.querySelector('#header-component')
    );
    if (!header) return;

    const bottom = header.getBoundingClientRect().bottom;

    // position:fixed 의 실제 containing block이 viewport가 아닐 수 있다
    // (transform/filter/perspective/will-change/contain 을 가진 가장 가까운 조상).
    // 그 조상의 viewport 기준 top만큼을 빼서 containing block 기준 top으로 맞춘다.
    let containingBlockTop = 0;
    let ancestor = header.parentElement;
    while (ancestor && ancestor !== document.body) {
      const cs = getComputedStyle(ancestor);
      const isContainingBlock =
        cs.transform !== 'none' ||
        cs.perspective !== 'none' ||
        cs.filter !== 'none' ||
        cs.backdropFilter !== 'none' ||
        /transform|perspective|filter/.test(cs.willChange) ||
        cs.contain.includes('paint') ||
        cs.contain.includes('strict') ||
        cs.contain.includes('content');
      if (isContainingBlock) {
        containingBlockTop = ancestor.getBoundingClientRect().top;
        break;
      }
      ancestor = ancestor.parentElement;
    }

    this.style.top = `${bottom - containingBlockTop}px`;
    this.style.height = `calc(100dvh - ${bottom}px)`;
  }

  resetToMain() {
    this.history = [];
    this.currentPanelId = "main";

    this.panels.forEach((el) => {
      const panel = /** @type {HTMLElement} */ (el);
      panel.dataset.active = "false";
      panel.dataset.pushed = "false";
    });

    const mainPanel = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-panel-id="main"]')
    );
    if (mainPanel) {
      mainPanel.dataset.active = "true";
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
}

if (!customElements.get("mobile-slide-menu")) {
  customElements.define("mobile-slide-menu", MobileSlideMenu);
}

document.addEventListener("DOMContentLoaded", () => {
  const triggerButtons = document.querySelectorAll(
    "[data-mobile-menu-trigger]"
  );

  triggerButtons.forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      const menu = /** @type {MobileSlideMenu | null} */ (
        document.querySelector("mobile-slide-menu")
      );
      if (menu) {
        menu.toggle();
      }
    });
  });
});

/**
 * @typedef {Object} MobileSlideMenuHelper
 * @property {function(): void} open
 * @property {function(): void} close
 * @property {function(): void} toggle
 */

/** @type {MobileSlideMenuHelper} */
const MobileSlideMenuHelper = {
  open() {
    const menu = /** @type {MobileSlideMenu | null} */ (
      document.querySelector("mobile-slide-menu")
    );
    if (menu) menu.open();
  },

  close() {
    const menu = /** @type {MobileSlideMenu | null} */ (
      document.querySelector("mobile-slide-menu")
    );
    if (menu) menu.close();
  },

  toggle() {
    const menu = /** @type {MobileSlideMenu | null} */ (
      document.querySelector("mobile-slide-menu")
    );
    if (menu) menu.toggle();
  },
};

// @ts-ignore
window.MobileSlideMenuHelper = MobileSlideMenuHelper;
